/* eslint-disable max-lines */
import Image from '@base/FutureImage';
import styled from '@emotion/styled';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import Button from 'aquilla/core/molecules/Button';
import Link from '@base/Link/Link';
import { segment } from '@actions/analytics';
import useCentreInfo from '@api/hooks/citySpecificOfflineCentres/useCentreInfo';
import { useRouter } from 'next/router';
import useWindowSize from '@hooks/useWindowSize';
import Typography from 'aquilla/core/atoms/Typography';
import { getIsLoggedIn } from '@api/selectors/auth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import Slide from '@material-ui/core/Slide';
import MUIDialog from '@material-ui/core/Dialog';
import YouTube from '@cont/FreeLesson/externalyoutube';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useOfflineCentreStore from '@stores/city-specific-offline-centres';
import { useEffect, useState, forwardRef } from 'react';
import Play from 'aquilla/icons/actions/Play';
import LottieAnimation from '@base/LottieAnimation/LottieAnimation';
import { getAnimationPath } from '@utils/animationHelpers';

import { OFFLINE_VIDEO_TOUR_PLAYED } from '@cont/OfflineCentres/constants';
import pushRoute from '@utils/pushRoute';
import {
  CENTRE_GET_DIRECTIONS,
  CENTRE_DOWNLOAD_BROCHURE,
  OFFLINE_CENTRE_SOURCE,
  OFFLINE_VISIT_THE_CENTRE
} from './constants';
import GridContainer from './CommonComponents';

const StyGridContainer = styled(GridContainer)`
  ${mediaQuery.uptoMobile} {
    padding-bottom: ${Spacings.SPACING_0B};
    padding-top: ${Spacings.SPACING_8B};
    border-bottom: solid ${Spacings.SPACING_0B} var(--color-violet-1);
  }
`;
const StyLink = styled(Link)`
  margin-top: ${Spacings.SPACING_1B};
`;

const LeftDiv = styled.div`
  display: flex;
  flex-direction: column;
  width: 512px;
  height: 336px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: auto;
    text-align: center;
  }
`;
const RightDiv = styled.div`
  display: flex;
  height: 336px;
  width: 608px;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: 200px;
  }
`;

const AddressDirectionWrapper = styled.div`
  display: inline;
  flex-direction: column;
  flex-wrap: wrap;
  ${mediaQuery.uptoMobile} {
    margin-left: ${Spacings.SPACING_1B};
  }
  margin-left: ${Spacings.SPACING_2B};
`;

const StyGetDirections = styled(Typography)`
  text-decoration: underline;
  display: inline;
  white-space: nowrap;
`;

const Container = styled.div`
  grid-column: span 24;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: ${Spacings.SPACING_16B};
  margin-bottom: ${Spacings.SPACING_26B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_6B};
    margin-bottom: ${Spacings.SPACING_0B};
    flex-direction: column;
  }
  width: 100%;
`;

const GoalDataWrapper = styled.div`
  display: flex;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    justify-content: center;
  }
`;

const StyH5 = styled(Typography)`
  color: var(--color-i-blue-23);
  margin-left: ${Spacings.SPACING_1B};
  margin-right: ${Spacings.SPACING_1B};
`;
const AddressText = styled.div`
  display: flex;
  flex-shrink: 0;
`;

const StyH1 = styled(Typography)``;
const StyH6 = styled(Typography)`
  display: block;
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    text-decoration: underline;
  }
`;
const CentreNameWrapper = styled.div`
  margin-bottom: ${Spacings.SPACING_3B};
  margin-top: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_3B};
  }
`;
const AddressWrapper = styled.div`
  display: flex;
  width: 450px;
  align-items: flex-start;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;
const ButtonWrapper = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_6B};
    margin-bottom: ${Spacings.SPACING_6B};
    width: 100%;
    align-items: center;
    justify-content: space-between;
  }
`;

const VisitCentre = styled(Button)`
  margin-right: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_0B};
    width: 100%;
    margin-right: ${Spacings.SPACING_4B};
  }
`;
const DownloadBrochure = styled(Button)`
  margin-right: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_0B};
    width: 100%;
    margin-right: ${Spacings.SPACING_0B};
  }
`;

const VideoCardContainer = styled.div`
  position: relative;
  overflow: hidden;
  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const PlayBtn = styled(Button)`
  padding: 0px;
  position: absolute;
  cursor: pointer;
  width: 170px;
  height: ${Spacings.SPACING_12B};
  border-radius: ${Spacings.SPACING_6B};
  border: 0px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  & > .button--label-content {
    display: flex;
    font-weight: ${Fonts.SEMIBOLD};
  }
  & > .button--right-icon {
    display: flex;
    margin-right: ${Spacings.SPACING_15};
  }
  ${mediaQuery.uptoMobile} {
    width: 155px;
    height: ${Spacings.SPACING_9B};
  }
`;

const StyledImg = styled(Image)`
  border-radius: ${Spacings.SPACING_2B};
  width: 608px !important;
  ${mediaQuery.uptoMobile} {
    width: 100% !important;
    height: 200px;
  }
`;

const ScholarShipData = styled.div`
  margin-top: ${Spacings.SPACING_4B};
  display: flex;
  align-items: center;
  margin-right: ${Spacings.SPACING_1B};
`;

const Stats = styled(Typography)`
  margin-left: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.SEMIBOLD};
`;

const AnimationContainer = styled(LottieAnimation)`
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_4B};
`;

const AnimationFallBackImage = ({ imageURL }) => {
  return <Image src={imageURL} width={80} height={80} alt="doubt-state-card" />;
};

const Transition = forwardRef(function Transition(props, ref) {
  // @ts-ignore
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(MUIDialog)`
  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    position: absolute;
    background: var(--color-text-primary);
    height: 80%;
    width: 88%;
    max-width: 88%;
    ${mediaQuery.uptoMobile} {
      margin: 16px auto;
      height: 30%;
    }
  }
`;
const opts = {
  height: '100%',
  width: '100%',
  playerVars: {
    autoplay: 1,
    showinfo: 0,
    rel: 0
  }
};

const VideoCard = ({
  data: { videoUrl, videoThumbnail, videoCta },
  src,
  centreInfo
}) => {
  const { isMobile } = useWindowSize();
  const [isOpen, setIsOpen] = useState(false);
  const {
    query: { goalUID: routeGoalUID }
  } = useRouter();

  const { centreName: cmsCentreName, cityName: cmsCityName } = centreInfo || {};
  const handleClick = () => {
    setIsOpen(true);
    segment.track(OFFLINE_VIDEO_TOUR_PLAYED, {
      tour_source: src,
      centre_name: cmsCentreName,
      city: cmsCityName,
      goal_uid: routeGoalUID
    });
  };
  return (
    <VideoCardContainer>
      <StyledImg
        src={videoThumbnail}
        width={isMobile ? '378' : '608'}
        height={isMobile ? '200' : '336'}
        alt="thumbnail"
      />
      <PlayBtn
        startIcon={
          <Play
            height={isMobile ? '11' : '14'}
            width={isMobile ? '11' : '14'}
          />
        }
        width={isMobile ? '155px' : '157'}
        height={isMobile ? '36px' : '60'}
        alt="play"
        onClick={handleClick}
        label={videoCta}
        variant="secondary"
      />
      <StyledDialog
        open={isOpen}
        // @ts-ignore
        TransitionComponent={Transition}
        onClose={() => setIsOpen(false)}
        $center
        $height="80%"
        $width="88%"
      >
        <YouTube
          opts={opts}
          videoId={videoUrl}
          onEnd={() => setIsOpen(false)}
          useWrapper={false}
        />
      </StyledDialog>
    </VideoCardContainer>
  );
};

const loadingStateAnimation = (
  <AnimationContainer
    Fallback={
      <AnimationFallBackImage imageURL="offline-centres/membersAttempting.svg" />
    }
    src={getAnimationPath('offlineCentres/JoiningMembers.json')}
  />
);

const CentreAddressBlock = ({ data, src }) => {
  const {
    address,
    centreTitle,
    cta1New,
    cta1NewDeepLink,
    cta2,
    goalInfo,
    cta2Link,
    latitude,
    longitude,
    showDirections,
    showAddress,
    showCtas,
    socialProofingText
  } = data;

  const { openElement: openLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const {
    query: { goalUID: routeGoalUID, centreUID }
  } = useRouter();

  const { centreInfo } = useCentreInfo(routeGoalUID, centreUID);

  const { isMobile } = useWindowSize();

  const {
    isDownloadBrochureClicked,
    setDownloadBrochureClicked,
    setBookAVisitFlow,
    setBookAVisitClicked,
    isBookAVisitClicked,
    selectedCentreBEData,
    isBookAVisitEnabledFromCMS,
    setCentreAddress,
    setGmapLink
  } = useOfflineCentreStore();

  const { centreName: cmsCentreName, cityName: cmsCityName } = centreInfo || {};
  const { city, state, name: centreName } = selectedCentreBEData || {};

  useEffect(() => {
    if (isLoggedIn) {
      if (isBookAVisitClicked) {
        setBookAVisitClicked(false);
        setBookAVisitFlow(true);
      }
      if (isDownloadBrochureClicked) {
        setDownloadBrochureClicked(false);
        segment.track(CENTRE_DOWNLOAD_BROCHURE, {
          centre_name: centreName,
          goal_uid: routeGoalUID,
          download_brochure_flow_type: OFFLINE_CENTRE_SOURCE,
          city,
          state
        });
        window.open(cta2Link, '_blank');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);
  const downloadBrochure = () => {
    if (!isLoggedIn) {
      setDownloadBrochureClicked(true);
      openLoginDrawer();
      return;
    }
    segment.track(CENTRE_DOWNLOAD_BROCHURE, {
      centre_name: centreName || cmsCentreName,
      goal_uid: routeGoalUID,
      download_brochure_flow_type: OFFLINE_CENTRE_SOURCE,
      city: city || cmsCityName,
      state
    });
    window.open(cta2Link, '_blank');
  };
  // make intro block
  const directionLink = `http://maps.google.com/maps?q=${latitude},${longitude}`;

  useEffect(() => {
    setGmapLink(directionLink);
    setCentreAddress(address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directionLink, address]);

  const handleVisitCentre = () => {
    // eslint-disable-next-line no-constant-condition
    segment.track(OFFLINE_VISIT_THE_CENTRE, {
      centre_name: centreName || cmsCentreName,
      city,
      state,
      goal_uid: routeGoalUID,
      block_position: 'Top'
    });
    if (!isLoggedIn) {
      openLoginDrawer();
      if (isBookAVisitEnabledFromCMS) setBookAVisitClicked(true);
    } else if (isBookAVisitEnabledFromCMS) {
      setBookAVisitFlow(true);
    } else pushRoute(cta1NewDeepLink, null, null, null, null, false);
  };
  const onGetDirectionsClick = () => {
    segment.track(CENTRE_GET_DIRECTIONS, {
      centre_name: centreName,
      city: city || cmsCityName,
      goal_uid: routeGoalUID
    });
  };
  return (
    <StyGridContainer>
      <Container>
        <LeftDiv>
          <GoalDataWrapper>
            <Image
              src="/offline-centres/blueStar.svg"
              width={12}
              height={12}
              alt="blue-star"
            />
            <StyH5 variant="h5">{goalInfo}</StyH5>
            <Image
              src="/offline-centres/blueStar.svg"
              width={12}
              height={12}
              alt="blue-star"
            />
          </GoalDataWrapper>
          <CentreNameWrapper>
            <StyH1 variant={isMobile ? 'h2' : 'h1'}>{centreTitle}</StyH1>
          </CentreNameWrapper>
          <AddressWrapper>
            {showAddress && (
              <AddressText>
                <Image
                  src="/offline-centres/location.svg"
                  width={20}
                  height={20}
                  alt="location-mark"
                />
              </AddressText>
            )}
            <AddressDirectionWrapper>
              {showAddress && !isMobile && (
                <StyH6 variant="h6">{address} &nbsp;</StyH6>
              )}
              {showDirections && (
                <StyLink
                  href={directionLink}
                  onClick={onGetDirectionsClick}
                  newTab
                >
                  {isMobile ? (
                    <StyH6 variant="h6">{address} &nbsp;</StyH6>
                  ) : (
                    <StyGetDirections variant="h6">
                      Get directions
                    </StyGetDirections>
                  )}
                </StyLink>
              )}
            </AddressDirectionWrapper>
          </AddressWrapper>
          {showCtas && (
            <ButtonWrapper>
              {cta1New && (
                <VisitCentre
                  label={cta1New}
                  onClick={handleVisitCentre}
                  color="black"
                  size={isMobile ? 'medium' : 'large'}
                />
              )}
              {cta2 && (
                <DownloadBrochure
                  label={cta2}
                  onClick={downloadBrochure}
                  variant="secondary"
                  color="black"
                  size={isMobile ? 'medium' : 'large'}
                />
              )}
            </ButtonWrapper>
          )}
          {!isMobile && (
            <ScholarShipData>
              {loadingStateAnimation}
              <Stats variant="p2">{socialProofingText}</Stats>
            </ScholarShipData>
          )}
        </LeftDiv>
        <RightDiv>
          <VideoCard data={data} src={src} centreInfo={centreInfo} />
        </RightDiv>
      </Container>
    </StyGridContainer>
  );
};

export default CentreAddressBlock;
